import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Copy, Check, Mail, Globe2, Palette, Image as ImageIcon, Type } from "lucide-react";
import { toast } from "sonner";

const CONTENT = {
  fr: {
    lang_switch: "English",
    title: "Kit presse & partenaires",
    subtitle: "Un Avocat en Or — Robin Oschmann · BCE 0838.152.848",
    intro:
      "Bienvenue journalistes, ONG, syndicats, mutuelles, et partenaires. Voici tout ce dont vous avez besoin pour parler d'Un Avocat en Or : logos haute résolution, descriptifs officiels, chiffres clés, et notre charte graphique.",
    logos_title: "Logos officiels",
    logos_desc: "Cliquez sur un logo pour le télécharger en haute résolution. Merci de ne pas modifier les couleurs, ne pas déformer, et de laisser une zone de respiration égale à 1/4 de la hauteur du blason.",
    descs_title: "Descriptifs officiels",
    descs_short: "Court (250 caractères)",
    descs_med: "Moyen (600 caractères)",
    descs_long: "Long (1500 caractères)",
    stats_title: "Chiffres clés & identité",
    palette_title: "Charte graphique",
    typo_title: "Typographies",
    contact_title: "Contact presse & partenariats",
    contact_desc: "Robin Oschmann, fondateur — disponible pour interviews, démonstrations, et partenariats B2B (ONG, syndicats, mutuelles, entreprises).",
    press: "Presse & médias",
    b2b: "Partenariats B2B",
    site: "Site officiel",
    copy_ok: "Copié !",
    copy_short: "Copier le texte",
    download: "Télécharger",
    short_desc:
      "Un Avocat en Or est une application belge d'assistance juridique alimentée par IA. 13 touches SOS pour 13 situations d'urgence, en 13 langues avec support droite-à-gauche. Souverain européen, RGPD, sans tracker publicitaire.",
    med_desc:
      "Un Avocat en Or est une application PWA multilingue qui met un avocat de poche à disposition de chaque citoyen face aux abus de pouvoir, harcèlement, arnaques numériques, urgences médicales ou agressions. L'application propose 13 touches SOS spécialisées, chacune générant en 3 secondes chrono : la loi citée, un courrier prêt à envoyer, et les contacts officiels géolocalisés. Éditée en Belgique par Robin Oschmann (BCE 0838.152.848), l'application est hébergée en Union européenne, 100 % RGPD, sans tracker publicitaire ni revente de données.",
    long_desc:
      "Un Avocat en Or (unavocatenor.com) est une application juridique de poche, alimentée par intelligence artificielle, conçue pour intervenir en 3 secondes chrono dans toutes les situations où un citoyen ordinaire se retrouve seul face à une injustice ou une urgence.\n\nL'application propose 13 touches SOS spécialisées couvrant : urgences médicales, contrôles policiers abusifs, conflits publics, abus administratifs (notaire, avocat, huissier), messages de phishing, appels frauduleux (deepfake), scan de documents officiels, harcèlement au travail et à l'école, maltraitance animale, parcours pour personnes sans-papiers, agressions LGBTQI+phobes, et SOS muet pour personnes ne pouvant parler.\n\nChaque touche déclenche en quelques secondes : une analyse IA (Claude 4.5) du contexte, la citation exacte des lois applicables (droit belge, français, luxembourgeois et suisse), un ou plusieurs courriers prêts à envoyer (syndicat, procureur, inspection du travail, Comité P, ordre professionnel…), et des contacts officiels géolocalisés (avocats spécialisés, ONG, permanences).\n\nL'application est disponible en 13 langues (Français, Anglais, Espagnol, Allemand, Arabe, Russe, Ukrainien, Roumain, Turc, Farsi, Pashto, Tigrinya, Lingala) avec un support natif droite-à-gauche pour les langues arabe, persane et pashto. Elle inclut également un mode d'accessibilité daltonienne (chaque touche annonce sa couleur en toutes lettres) et un réseau anonyme d'hébergement solidaire pour les victimes LGBTQI+.\n\nÉditée par Robin Oschmann, entrepreneur individuel belge (BCE 0838.152.848), l'application est hébergée en Union européenne, conforme RGPD, sans traqueur publicitaire ni revente de données. Le modèle économique est freemium : gratuit à usage modéré, 9,50 € TTC/mois pour un usage individuel, 14,50 € TTC/mois pour une famille, et des packs B2B pour les ONG, syndicats, mutuelles et entreprises (à partir de 249 € HT/mois).",
  },
  en: {
    lang_switch: "Français",
    title: "Press & Partner Kit",
    subtitle: "A Lawyer in Gold — Robin Oschmann · VAT BE 0838.152.848",
    intro:
      "Welcome journalists, NGOs, unions, insurers, and partners. Here is everything you need to write about A Lawyer in Gold: high-resolution logos, official descriptions, key figures, and our visual identity.",
    logos_title: "Official logos",
    logos_desc: "Click on a logo to download the high-resolution version. Please do not alter colors, do not distort, and leave breathing space equal to 1/4 of the shield's height.",
    descs_title: "Official descriptions",
    descs_short: "Short (250 characters)",
    descs_med: "Medium (600 characters)",
    descs_long: "Long (1500 characters)",
    stats_title: "Key figures & identity",
    palette_title: "Visual identity",
    typo_title: "Typography",
    contact_title: "Press & partnership contact",
    contact_desc: "Robin Oschmann, founder — available for interviews, demonstrations, and B2B partnerships (NGOs, unions, insurers, enterprises).",
    press: "Press & media",
    b2b: "B2B partnerships",
    site: "Official website",
    copy_ok: "Copied!",
    copy_short: "Copy text",
    download: "Download",
    short_desc:
      "A Lawyer in Gold is a Belgian AI-powered legal assistance app. 13 SOS buttons for 13 emergency situations, in 13 languages with right-to-left support. European sovereign, GDPR-compliant, no advertising trackers.",
    med_desc:
      "A Lawyer in Gold is a multilingual PWA that puts a pocket lawyer in the hands of every citizen facing abuse of power, harassment, digital scams, medical emergencies or assault. The app offers 13 specialized SOS buttons, each generating in 3 seconds flat: the cited law, a ready-to-send letter, and geolocated official contacts. Published in Belgium by Robin Oschmann (VAT BE 0838.152.848), the app is hosted in the European Union, fully GDPR-compliant, with no advertising trackers or data resale.",
    long_desc:
      "A Lawyer in Gold (unavocatenor.com) is an AI-powered pocket legal application designed to intervene in 3 seconds flat in every situation where an ordinary citizen finds themselves alone against injustice or an emergency.\n\nThe app offers 13 specialized SOS buttons covering: medical emergencies, abusive police checks, public conflicts, administrative abuse (notary, lawyer, bailiff), phishing messages, fraudulent calls (deepfake), scanning of official documents, workplace and school harassment, animal mistreatment, journeys for undocumented persons, LGBTQI+ assaults, and silent SOS for people unable to speak.\n\nEach button triggers within seconds: an AI analysis (Claude 4.5) of the context, exact citation of applicable laws (Belgian, French, Luxembourg and Swiss law), one or more ready-to-send letters (union, prosecutor, labor inspection, oversight body, professional order…), and geolocated official contacts (specialized lawyers, NGOs, hotlines).\n\nThe app is available in 13 languages (French, English, Spanish, German, Arabic, Russian, Ukrainian, Romanian, Turkish, Farsi, Pashto, Tigrinya, Lingala) with native right-to-left support for Arabic, Persian and Pashto. It also includes a color-blind accessibility mode (each button announces its color in words) and an anonymous solidarity hosting network for LGBTQI+ victims.\n\nPublished by Robin Oschmann, Belgian sole proprietor (VAT BE 0838.152.848), the app is hosted in the European Union, GDPR-compliant, with no advertising trackers or data resale. The business model is freemium: free for light use, €9.50/month for individual use, €14.50/month for a family, and B2B packs for NGOs, unions, insurers and enterprises (from €249/month).",
  },
};

const LOGOS = [
  {
    file: "logo-seal.png",
    label_fr: "Blason principal (compact)",
    label_en: "Main shield (compact)",
    size: "786 Ko",
    dim: "carré · 1200×1200 px",
    tip_fr: "Email, documents Word/PDF, favicons",
    tip_en: "Email, Word/PDF documents, favicons",
  },
  {
    file: "logo-seal-large.png",
    label_fr: "Blason haute résolution",
    label_en: "Shield high resolution",
    size: "2,7 Mo",
    dim: "carré · 2400×2400 px",
    tip_fr: "Impression, affiche, kakémono, presse",
    tip_en: "Print, poster, banner, press",
  },
  {
    file: "logo-hand-drawn.png",
    label_fr: "Version dessin à la main",
    label_en: "Hand-drawn version",
    size: "2,4 Mo",
    dim: "carré · 2000×2000 px",
    tip_fr: "Fond noir, présentations, réseaux sociaux",
    tip_en: "Dark background, decks, social networks",
  },
  {
    file: "logo-hand-drawn-512.png",
    label_fr: "Version 512 px (compact)",
    label_en: "512 px version (compact)",
    size: "520 Ko",
    dim: "carré · 512×512 px",
    tip_fr: "Favicon, avatar Twitter/LinkedIn",
    tip_en: "Favicon, Twitter/LinkedIn avatar",
  },
];

const COLORS = [
  { name: "Or profond", hex: "#B8860B", desc_fr: "Couleur signature — logo, CTA premium", desc_en: "Signature color — logo, premium CTA" },
  { name: "Ambre", hex: "#F59E0B", desc_fr: "Accents, badges, mises en valeur", desc_en: "Accents, badges, highlights" },
  { name: "Noir profond", hex: "#0A0A0A", desc_fr: "Fond premium, contraste maximal", desc_en: "Premium background, maximum contrast" },
  { name: "Ardoise 900", hex: "#0F172A", desc_fr: "Textes principaux, headers", desc_en: "Main text, headers" },
  { name: "Rouge SOS", hex: "#B91C1C", desc_fr: "Urgences vitales, touche 13", desc_en: "Vital emergencies, button 13" },
  { name: "Rose harcèlement", hex: "#BE185D", desc_fr: "Touche 8 — travail & école", desc_en: "Button 8 — work & school" },
];

function CopyableTextarea({ text, dataId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };
  return (
    <div className="mt-2">
      <textarea
        readOnly
        value={text}
        className="w-full min-h-[110px] text-xs p-3 rounded-sm border border-slate-300 bg-white text-slate-800 font-mono resize-y"
        data-testid={`${dataId}-textarea`}
      />
      <button
        onClick={copy}
        className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-slate-900 hover:bg-slate-800 text-white transition"
        data-testid={`${dataId}-copy-btn`}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copié" : "Copier"}
      </button>
    </div>
  );
}

export default function MediaKit() {
  const [lang, setLang] = useState("fr");
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="media-kit-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900" data-testid="media-kit-back">
            <ArrowLeft className="h-4 w-4" />
            {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <button
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition"
            data-testid="media-kit-lang-toggle"
          >
            <Globe2 className="h-3.5 w-3.5" />
            {c.lang_switch}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 flex items-center justify-center">
            <ImageIcon className="h-5 w-5" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif text-slate-900" data-testid="media-kit-title">
            {c.title}
          </h1>
        </div>
        <p className="text-sm text-slate-600">{c.subtitle}</p>
        <p className="mt-6 text-base text-slate-700 leading-relaxed max-w-3xl">{c.intro}</p>

        {/* LOGOS */}
        <section className="mt-12" data-testid="section-logos">
          <h2 className="text-2xl font-serif text-slate-900 mb-2">{c.logos_title}</h2>
          <p className="text-sm text-slate-600 max-w-3xl">{c.logos_desc}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {LOGOS.map((logo, i) => (
              <div key={i} className="rounded-sm border border-slate-200 bg-white p-4 flex flex-col" data-testid={`logo-card-${i}`}>
                <div className="aspect-square bg-black rounded-sm flex items-center justify-center overflow-hidden">
                  <img src={`/${logo.file}`} alt={logo.label_fr} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="mt-3 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{lang === "fr" ? logo.label_fr : logo.label_en}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{logo.dim} · {logo.size}</div>
                  <div className="text-xs text-slate-600 mt-2 italic">{lang === "fr" ? logo.tip_fr : logo.tip_en}</div>
                </div>
                <a
                  href={`/${logo.file}`}
                  download
                  className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-sm bg-slate-900 hover:bg-slate-800 text-white transition"
                  data-testid={`logo-download-${i}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  {c.download}
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* DESCRIPTIONS */}
        <section className="mt-14" data-testid="section-descs">
          <div className="flex items-center gap-2 mb-2">
            <Type className="h-5 w-5 text-amber-700" />
            <h2 className="text-2xl font-serif text-slate-900">{c.descs_title}</h2>
          </div>
          <div className="mt-6 space-y-6">
            <div className="rounded-sm border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">{c.descs_short}</div>
              <CopyableTextarea text={c.short_desc} dataId="desc-short" />
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">{c.descs_med}</div>
              <CopyableTextarea text={c.med_desc} dataId="desc-med" />
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">{c.descs_long}</div>
              <CopyableTextarea text={c.long_desc} dataId="desc-long" />
            </div>
          </div>
        </section>

        {/* STATS / IDENTITY */}
        <section className="mt-14" data-testid="section-stats">
          <h2 className="text-2xl font-serif text-slate-900 mb-4">{c.stats_title}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-3xl font-serif text-amber-700">13</div>
              <div className="text-xs text-slate-600 mt-1">{lang === "fr" ? "touches SOS" : "SOS buttons"}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-3xl font-serif text-amber-700">13</div>
              <div className="text-xs text-slate-600 mt-1">{lang === "fr" ? "langues (dont 3 RTL)" : "languages (incl. 3 RTL)"}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-3xl font-serif text-amber-700">4</div>
              <div className="text-xs text-slate-600 mt-1">{lang === "fr" ? "juridictions (BE/FR/LU/CH)" : "jurisdictions (BE/FR/LU/CH)"}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-3xl font-serif text-amber-700">3s</div>
              <div className="text-xs text-slate-600 mt-1">{lang === "fr" ? "temps de réponse" : "response time"}</div>
            </div>
          </div>
          <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5 text-sm text-slate-700 space-y-1">
            <div><strong>{lang === "fr" ? "Éditeur" : "Publisher"} :</strong> Robin Oschmann</div>
            <div><strong>BCE / VAT :</strong> BE 0838.152.848</div>
            <div><strong>{lang === "fr" ? "Siège" : "Registered office"} :</strong> 54 rue Fond du Sart, 4171 Comblain-au-Pont, Belgique</div>
            <div><strong>{lang === "fr" ? "Site" : "Website"} :</strong> <a href="https://unavocatenor.com" className="text-amber-700 hover:underline">unavocatenor.com</a></div>
            <div><strong>{lang === "fr" ? "Fondation" : "Founded"} :</strong> 2025 · {lang === "fr" ? "Lancement public" : "Public launch"} : 2026</div>
          </div>
        </section>

        {/* PALETTE */}
        <section className="mt-14" data-testid="section-palette">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="h-5 w-5 text-amber-700" />
            <h2 className="text-2xl font-serif text-slate-900">{c.palette_title}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {COLORS.map((col, i) => (
              <div key={i} className="rounded-sm border border-slate-200 bg-white overflow-hidden" data-testid={`color-${i}`}>
                <div className="h-16" style={{ backgroundColor: col.hex }} />
                <div className="p-3">
                  <div className="text-sm font-semibold text-slate-900">{col.name}</div>
                  <div className="text-xs font-mono text-slate-500 mt-0.5">{col.hex}</div>
                  <div className="text-xs text-slate-600 mt-2">{lang === "fr" ? col.desc_fr : col.desc_en}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <Type className="h-5 w-5 text-amber-700" />
              <h3 className="text-lg font-semibold text-slate-900">{c.typo_title}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <div className="text-2xl font-serif text-slate-900">Playfair Display</div>
                <div className="text-xs text-slate-500 mt-1">{lang === "fr" ? "Titres & branding" : "Headings & branding"}</div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <div className="text-2xl text-slate-900" style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}>Inter / System UI</div>
                <div className="text-xs text-slate-500 mt-1">{lang === "fr" ? "Corps de texte" : "Body text"}</div>
              </div>
              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <div className="text-2xl text-amber-900" style={{ fontFamily: "'UnifrakturMaguntia', serif" }}>Un Avocat en Or</div>
                <div className="text-xs text-slate-500 mt-1">{lang === "fr" ? "Banderole gothique (accent)" : "Gothic banner (accent)"}</div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section className="mt-14 rounded-sm border border-amber-300 bg-amber-50 p-6" data-testid="section-contact">
          <h2 className="text-2xl font-serif text-slate-900 mb-2">{c.contact_title}</h2>
          <p className="text-sm text-slate-700 mb-4">{c.contact_desc}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="mailto:contact@unavocatenor.com"
              className="rounded-sm bg-white border border-amber-300 p-4 hover:border-amber-500 transition"
              data-testid="contact-press"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Mail className="h-4 w-4 text-amber-700" />
                {c.press}
              </div>
              <div className="text-xs text-amber-800 mt-1 underline">contact@unavocatenor.com</div>
            </a>
            <a
              href="mailto:pro@unavocatenor.com"
              className="rounded-sm bg-white border border-amber-300 p-4 hover:border-amber-500 transition"
              data-testid="contact-b2b"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Mail className="h-4 w-4 text-amber-700" />
                {c.b2b}
              </div>
              <div className="text-xs text-amber-800 mt-1 underline">pro@unavocatenor.com</div>
            </a>
            <a
              href="https://unavocatenor.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm bg-white border border-amber-300 p-4 hover:border-amber-500 transition"
              data-testid="contact-site"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Globe2 className="h-4 w-4 text-amber-700" />
                {c.site}
              </div>
              <div className="text-xs text-amber-800 mt-1 underline">unavocatenor.com</div>
            </a>
          </div>
        </section>

        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-4 text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
          <Link to="/terms" className="hover:text-slate-900 underline">
            {lang === "fr" ? "Conditions d'utilisation" : "Terms of Service"}
          </Link>
          <Link to="/" className="hover:text-slate-900 underline">
            unavocatenor.com
          </Link>
        </div>
      </main>
    </div>
  );
}
